/**
 * useReportTemplates Hook (Story 5.3)
 *
 * Fetches report templates from the API and parses their layout JSON.
 * Returns templates with parsed layout data ready for gallery display.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";
import type { TemplateLayout, ParsedTemplate } from "../lib/templateUtils";

const DEFAULT_LAYOUT: TemplateLayout = {
  version: 1,
  grid: { columns: 12 },
  widgets: [],
};

export function useReportTemplates() {
  const {
    data: templates = [],
    isLoading,
    error,
  } = useQuery<ParsedTemplate[]>({
    queryKey: ["report-templates"],
    queryFn: async () => {
      const res = await apiGet("/api/report-templates");
      if (!res.ok) throw new Error("Failed to fetch templates");
      const json = await res.json();
      const rawTemplates = json.data.templates as Array<
        Omit<ParsedTemplate, "parsedLayout">
      >;
      return rawTemplates.map((t) => {
        let parsedLayout: TemplateLayout;
        let parseError = false;
        try {
          parsedLayout = JSON.parse(t.layoutJson) as TemplateLayout;
        } catch {
          parsedLayout = DEFAULT_LAYOUT;
          parseError = true;
        }
        // Null-coalesce format for backward compat with legacy API responses
        const format = (t.format ?? parsedLayout.format ?? "document") as "document" | "slide-deck";
        return { ...t, parsedLayout, parseError, format };
      });
    },
  });

  return { templates, isLoading, error };
}
